import React, { useCallback, useEffect, useRef, useState } from "react";
import { array, bool, func, object } from "prop-types";
import get from "lodash/get";
import "./faqcard.m.css";

const FaqCard = ({ story, faqData = [], isfaqPopup, onFaqVisibilityChange, isNoscroll = false, onFaqInteraction }) => {
  const isTextstory = get(story, ["story-template"], "") === "text";
  const [activeIndexes, setActiveIndexes] = useState([0]);
  const [isMoreUpdateActive, setIsMoreUpdateActive] = useState(false);
  const [showMoreContent, setShowMoreContent] = useState(false);
  const [faqViewportBottomOffset, setFaqViewportBottomOffset] = useState(0);
  const faqHeadingRef = useRef(null);
  const faqVisibilityRef = useRef(false);
  const animationFrameRef = useRef(null);
  const faqHeadingVisibleThreshold = 0.8;
  const firstFaqs = faqData.slice(0, 3);
  const remainingFaqs = faqData.slice(3);

  const notifyFaqVisibilityChange = useCallback(
    (isVisible) => {
      if (typeof onFaqVisibilityChange !== "function" || faqVisibilityRef.current === isVisible) {
        return;
      }

      faqVisibilityRef.current = isVisible;
      onFaqVisibilityChange(isVisible);
    },
    [onFaqVisibilityChange]
  );

  const updateFaqButtonVisibility = useCallback(() => {
    if (isfaqPopup || typeof onFaqVisibilityChange !== "function") {
      return;
    }

    const headingElement = faqHeadingRef.current;
    if (!headingElement || typeof window === "undefined") {
      notifyFaqVisibilityChange(false);
      return;
    }

    const rect = headingElement.getBoundingClientRect();
    const viewportBottom = window.innerHeight - faqViewportBottomOffset;
    const visibleTriggerPoint = viewportBottom - rect.height * faqHeadingVisibleThreshold;

    // FAQ button should be visible only when user is above FAQ heading,
    // until around 40% of the heading is visible in the readable viewport.
    const isUserAboveFaq = rect.top > visibleTriggerPoint;
    notifyFaqVisibilityChange(!isUserAboveFaq);
  }, [
    faqViewportBottomOffset,
    isfaqPopup,
    notifyFaqVisibilityChange,
    onFaqVisibilityChange,
    faqHeadingVisibleThreshold,
  ]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const getVisibleHeight = (element) => {
      if (!element) return 0;

      const styles = window.getComputedStyle(element);
      if (styles.display === "none" || styles.visibility === "hidden") {
        return 0;
      }

      const rect = element.getBoundingClientRect();
      if (rect.height <= 0 || rect.width <= 0) {
        return 0;
      }

      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      return Math.ceil(visibleHeight);
    };

    const updateBottomOffset = () => {
      const stickyAdSlot =
        document.getElementById("story-page-sticky_ad") || document.getElementById("story-page-sticky_ad-mobile");
      const stickyAdWrapper = stickyAdSlot?.closest(".ad-wrapper");
      const stickyAdContainer = stickyAdWrapper?.parentElement;

      const stickyAdHeight = Math.max(
        getVisibleHeight(stickyAdContainer),
        getVisibleHeight(stickyAdWrapper),
        getVisibleHeight(stickyAdSlot)
      );

      setFaqViewportBottomOffset((prev) => (prev === stickyAdHeight ? prev : stickyAdHeight));
    };

    const onViewportChange = () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = window.requestAnimationFrame(() => {
        updateBottomOffset();
        updateFaqButtonVisibility();
      });
    };

    onViewportChange();

    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, { passive: true });

    let resizeObserver;
    let mutationObserver;
    const stickyAdSlot =
      document.getElementById("story-page-sticky_ad") || document.getElementById("story-page-sticky_ad-mobile");
    const stickyAdWrapper = stickyAdSlot?.closest(".ad-wrapper");
    const stickyAdContainer = stickyAdWrapper?.parentElement;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(onViewportChange);
      [stickyAdContainer, stickyAdWrapper, stickyAdSlot]
        .filter(Boolean)
        .forEach((element) => resizeObserver.observe(element));
    }

    if (typeof MutationObserver !== "undefined") {
      mutationObserver = new MutationObserver(onViewportChange);
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [updateFaqButtonVisibility]);

  useEffect(() => {
    updateFaqButtonVisibility();
  }, [updateFaqButtonVisibility]);

  useEffect(() => {
    if (isfaqPopup) {
      // Open all items
      setActiveIndexes(faqData.slice(0, 2).map((_, index) => index));
    } else {
      // Default: only first open (optional)
      setActiveIndexes([0]);
    }
  }, [isfaqPopup, faqData]);

  useEffect(() => {
    return () => {
      if (!isfaqPopup && typeof onFaqVisibilityChange === "function") {
        notifyFaqVisibilityChange(false);
      }
    };
  }, [isfaqPopup, notifyFaqVisibilityChange, onFaqVisibilityChange]);

  const handleClick = (index) => {
    setActiveIndexes((prev) => {
      const isActive = prev.includes(index);
      if (isActive) {
        if (typeof onFaqInteraction === "function") {
          onFaqInteraction(isfaqPopup ? "overlay_collapse" : "widget_collapse", "close");
        }
        return prev.filter((i) => i !== index);
      } else {
        if (typeof onFaqInteraction === "function") {
          onFaqInteraction(isfaqPopup ? "overlay_expand" : "widget_expand", "open");
        }
        return [...prev, index];
      }
    });
  };

  if (!isTextstory || faqData.length === 0) {
    return null;
  }
  const handleReadmore = () => {
    setShowMoreContent(true);
  }

  return (
    <>
      <div styleName={`faq-box ${isfaqPopup ? "faqpopupbox" : ""}`}>
        <div styleName="faq-box-header">
          <h3 ref={!isfaqPopup ? faqHeadingRef : undefined}>Frequently Asked Questions</h3>
          {/* {isfaqPopup && (
            <button
              styleName="close-button"
              onClick={() => {
                if (typeof onFaqInteraction === "function") {
                  onFaqInteraction("overlay_close", "close");
                }
                if (setIsfaqPopup) {
                  setIsfaqPopup(false);
                }
              }}
              aria-label="Close FAQ"
            ></button>
          )} */}
        </div>

        <div styleName="faq-content-body">
          <div
            styleName={`faq-content-scrollbar ${showMoreContent ? 'defaultheight' : ''} ${isMoreUpdateActive || isfaqPopup ? "htauto" : ""} ${
              isNoscroll ? "noscroll" : ""
            }`}
          >
            {firstFaqs.map((item, index) => (
              <div key={index} styleName={`faq-content ${activeIndexes.includes(index) ? "active" : ""}`}>
                <div
                  styleName="faq-title"
                  onClick={() => {
                    handleClick(index);
                  }}
                >
                  <span>{item.title}</span>{" "}
                  <button styleName="toggle-button" aria-label="Toggle answer">
                    <i>{activeIndexes.includes(index) ? "-" : "+"}</i>
                  </button>
                </div>
                <p>{item.content}</p>
              </div>
            ))}
            {(isMoreUpdateActive || isfaqPopup) && (
              <>
                {remainingFaqs.map((item, index) => {
                  const realIndex = index + 3;

                  return (
                    <div key={realIndex} styleName={`faq-content ${activeIndexes.includes(realIndex) ? "active" : ""}`}>
                      <div styleName="faq-title" onClick={() => handleClick(realIndex)}>
                        <span>{item.title}</span>
                        <button styleName="toggle-button" aria-label="Toggle answer">
                          <i>{activeIndexes.includes(realIndex) ? "-" : "+"}</i>
                        </button>
                      </div>
                      <p>{item.content}</p>
                    </div>
                  );
                })}
              </>
            )}
          </div>
          {!isMoreUpdateActive && !isfaqPopup && (
            <button
              aria-label="Toggle more updates"
              onClick={() => {
                if (typeof onFaqInteraction === "function") {
                  onFaqInteraction("widget_viewmore", "faq");
                }
                setIsMoreUpdateActive(true);
              }}
              styleName={`${isMoreUpdateActive ? "active" : ""} moreupdates`}
            >
              More Questions
            </button>
          )}
          <div styleName="faq-disclaimer">
            <div styleName={`${showMoreContent ? 'readmoreheightauto' : ''} faq-disclaimer-readmore`}><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none">
              <mask id="mask0_10403_1238" maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                <rect width="14" height="14" fill="#D9D9D9"></rect>
              </mask>
              <g mask="url(#mask0_10403_1238)">
                <path
                  d="M6.41675 9.9165H7.58342V6.4165H6.41675V9.9165ZM7.00008 5.24984C7.16536 5.24984 7.3039 5.19393 7.41571 5.08213C7.52751 4.97032 7.58342 4.83178 7.58342 4.6665C7.58342 4.50123 7.52751 4.36268 7.41571 4.25088C7.3039 4.13907 7.16536 4.08317 7.00008 4.08317C6.8348 4.08317 6.69626 4.13907 6.58446 4.25088C6.47265 4.36268 6.41675 4.50123 6.41675 4.6665C6.41675 4.83178 6.47265 4.97032 6.58446 5.08213C6.69626 5.19393 6.8348 5.24984 7.00008 5.24984ZM7.00008 12.8332C6.19314 12.8332 5.4348 12.68 4.72508 12.3738C4.01536 12.0675 3.398 11.6519 2.873 11.1269C2.348 10.6019 1.93237 9.98456 1.62612 9.27484C1.31987 8.56512 1.16675 7.80678 1.16675 6.99984C1.16675 6.19289 1.31987 5.43456 1.62612 4.72484C1.93237 4.01512 2.348 3.39775 2.873 2.87275C3.398 2.34775 4.01536 1.93213 4.72508 1.62588C5.4348 1.31963 6.19314 1.1665 7.00008 1.1665C7.80703 1.1665 8.56536 1.31963 9.27508 1.62588C9.9848 1.93213 10.6022 2.34775 11.1272 2.87275C11.6522 3.39775 12.0678 4.01512 12.374 4.72484C12.6803 5.43456 12.8334 6.19289 12.8334 6.99984C12.8334 7.80678 12.6803 8.56512 12.374 9.27484C12.0678 9.98456 11.6522 10.6019 11.1272 11.1269C10.6022 11.6519 9.9848 12.0675 9.27508 12.3738C8.56536 12.68 7.80703 12.8332 7.00008 12.8332ZM7.00008 11.6665C8.30286 11.6665 9.40633 11.2144 10.3105 10.3103C11.2147 9.40609 11.6667 8.30262 11.6667 6.99984C11.6667 5.69706 11.2147 4.59359 10.3105 3.68942C9.40633 2.78525 8.30286 2.33317 7.00008 2.33317C5.6973 2.33317 4.59383 2.78525 3.68967 3.68942C2.7855 4.59359 2.33341 5.69706 2.33341 6.99984C2.33341 8.30262 2.7855 9.40609 3.68967 10.3103C4.59383 11.2144 5.6973 11.6665 7.00008 11.6665Z"
                  fill="rgba(var(--black), 0.4)"
                ></path>
              </g>
            </svg>{" "} Certain content on this page, including FAQs, and other supplementary informational features, maybe generated or assisted by artificial intelligence tools. While reasonable efforts are made to review and verify such content, AI generated output may occasionally contain errors, omissions or inconsistencies. Readers are advised to independently verify any information before relying upon them for professional, legal, financial, medical or other decisions. The publisher along with its affiliates and contributors do not warrant accuracy of AI-generated content and disclaim any liability, loss or damage arising from its use. {!showMoreContent && <button aria-label="read more" onClick={() => handleReadmore()} styleName={`${isfaqPopup ? "disclaimer-read-more" : "disclaimer-read-more disclaimer-read-more-widget"}`}>...read more</button>}</div>

          </div>
        </div>
      </div>
    </>
  );
};

FaqCard.propTypes = {
  story: object,
  faqData: array,
  isfaqPopup: bool,
  onFaqVisibilityChange: func,
  isNoscroll: bool,
  onFaqInteraction: func,
};

export default FaqCard;
